import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";

import Form from "antd/es/form";
import { FormInstance } from "antd/es/form/Form";
import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import SchemaFieldType from "@mapmycustomers/shared/enum/SchemaFieldType";
import Located from "@mapmycustomers/shared/types/base/Located";
import { Company, EntityType, Person } from "@mapmycustomers/shared/types/entity";
import { LayoutSchemaField } from "@mapmycustomers/shared/types/layout/FormLayout";
import User from "@mapmycustomers/shared/types/User";
import { Button } from "@mapmycustomers/ui";

import ButtonLink from "@app/component/ButtonLink";
import CircleColorPicker from "@app/component/CircleColorPicker";
import ConditionalFormField, {
  ConditionalFormFieldRenderProps,
} from "@app/component/ConditionalFormField";
import FormFields from "@app/component/FormFields";
import FormLayoutSelector from "@app/component/FormFields/components/FormLayoutSelector";
import OwnerField from "@app/component/FormFields/components/OwnerField";
import { PersonCompanyField } from "@app/component/FormFields/components/ParentCompanyField";
import { inheritAddressFromCompany } from "@app/component/FormFields/utils/inheritAddressFromCompany";
import FormNoLayoutAlert from "@app/component/FormNoLayoutAlert";
import AddressFormItem from "@app/component/preview/components/AddressFormItem";
import EmailTextFieldWithActions from "@app/component/preview/components/EmailTextFieldWithActions";
import PhoneTextFieldWithActions from "@app/component/preview/components/PhoneTextFieldWithActions";
import SaveChangesFooter from "@app/component/preview/components/SaveChangesFooter";
import personToFormValues from "@app/component/preview/PersonRecordPane/utils/personToFormValues";
import consts from "@app/component/preview/styles/_consts.module.scss";
import showCannotEditNotification from "@app/component/preview/util/showCannotEditNotification";
import showConfirmationIfOwnerChanged from "@app/component/preview/util/showConfirmationIfOwnerChanged";
import useBlankRequiredPrivateFields from "@app/component/preview/util/useBlankRequiredPrivateFieldsChecker";
import useDividerSpan from "@app/component/preview/util/useDividerSpan";
import OrganizationSetting from "@app/enum/OrganizationSetting";
import { EnterPinLocationModePayload } from "@app/scene/map/store/pinLocation/actions";
import {
  getOrganizationSettingValue,
  isCurrentUserOwner,
  isCurrentUserReadOnly,
} from "@app/store/iam";
import { getUsersOfEntireOrganization } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { findDifference } from "@app/util/arrayUtils";
import eventEmitter from "@app/util/eventEmitter";
import { CompanyFieldName } from "@app/util/fieldModel/CompanyFieldModel";
import { PersonFieldName } from "@app/util/fieldModel/PersonFieldModel";
import useAllowedUsers from "@app/util/hook/useAllowedUsers";
import useCustomFieldValidation from "@app/util/hook/useCustomFieldValidation";
import useErroredFieldsScroller from "@app/util/hook/useErroredFieldsScroller";
import useOnScreen from "@app/util/hook/useOnScreen";
import useSharedRef from "@app/util/hook/useSharedRef";
import { personLayoutModel } from "@app/util/layout/impl";

import AdditionalDetails from "../../components/AdditionalDetails";
import DeleteButton from "../../components/DeleteButton";
import HistoryButton from "../../components/HistoryButton";
import TabLayout from "../../components/TabLayout";
import { getAddress } from "../../util/getAddress";
import { deletePerson, updatePerson } from "../store/actions";
import { isDeleting } from "../store/selectors";
import getPersonPayloadFromFormData from "../utils/getPersonPayloadFromFormData";

import styles from "./Details.module.scss";
import DetailsFormValues from "./DetailsFormValues";

const excludeUnnecessaryFields = (schemaField: LayoutSchemaField) =>
  schemaField.fieldType !== SchemaFieldType.FILE;
const allOfThem = () => true;

const messages = defineMessages({
  saveChanges: {
    id: "personRecordPane.details.saveButton.changed",
    defaultMessage: "Save Changes",
    description: "Save button label if form changed",
  },
  saved: {
    id: "personRecordPane.saveButton.notChanged",
    defaultMessage: "Saved",
    description: "Save button label if form did not changed",
  },
  title: {
    id: "personRecordPane.details.title",
    defaultMessage: "Details",
    description: "Person details tab title",
  },
});

interface DetailsProps {
  allOrgUsers: User[];
  allUsersAllowedForOwnership: boolean;
  companies: Company[];
  deleting?: boolean;
  form: FormInstance<DetailsFormValues>;
  formChanged: boolean;
  isMapMode: boolean;
  isOwner: boolean;
  isReadOnlyMember: boolean;
  onAddressChange: (located: Located) => void;
  onDelete: typeof deletePerson.request;
  onEnterPinLocationMode?: (payload: EnterPinLocationModePayload) => void;
  onFormChange: (isChanged: boolean) => void;
  onHide: () => void;
  onUpdatePerson: typeof updatePerson.request;
  person: Person;
  personUpdateLoading: boolean;
}

const Details: React.FC<DetailsProps> = ({
  allOrgUsers,
  allUsersAllowedForOwnership,
  companies,
  deleting,
  form,
  formChanged,
  isMapMode,
  isOwner,
  isReadOnlyMember,
  onAddressChange,
  onDelete,
  onEnterPinLocationMode,
  onFormChange,
  onHide,
  onUpdatePerson,
  person,
  personUpdateLoading,
}) => {
  const intl = useIntl();

  const entityLayout = useMemo(() => personLayoutModel.getLayoutFor(person), [person]);
  const [formLayout, setFormLayout] = useState(entityLayout);
  const [primaryCompanyId, setPrimaryCompanyId] = useState<Company["id"] | undefined>(
    person.primaryAccountId ?? undefined
  );

  const [showInheritButton, setShowInheritButton] = useState<boolean>(false);
  useEffect(() => {
    setFormLayout(entityLayout);
  }, [entityLayout]);

  const blankRequiredPrivateFields = useBlankRequiredPrivateFields(
    formLayout.schema,
    EntityType.PERSON,
    person
  );
  const [cannotSave, setCannotSave] = useState(false);

  const handleFormChange = useCallback(() => {
    onFormChange(form.isFieldsTouched());
    if (blankRequiredPrivateFields.length) {
      setCannotSave(true);
      showCannotEditNotification(intl, EntityType.PERSON, blankRequiredPrivateFields);
    }
  }, [form, intl, onFormChange, blankRequiredPrivateFields]);

  const updateFormValues = useCallback(
    (person: Person, formUpdatedValues?: DetailsFormValues) => {
      const updatedFormValues = personToFormValues(person);
      const customFields = updatedFormValues.customFields;
      const customFormFields = Object.keys(customFields ?? {}).map((key) => {
        if (formUpdatedValues?.customFields && key in formUpdatedValues.customFields) {
          return {
            errors: [],
            name: ["customFields", key],
            touched: true,
            value: formUpdatedValues?.customFields?.[key],
          };
        }
        return {
          errors: [],
          name: ["customFields", key],
          touched: false,
          value: customFields?.[key],
        };
      });
      const fields = Object.keys(updatedFormValues).map((key) => {
        if (formUpdatedValues && key in formUpdatedValues) {
          return {
            name: key,
            touched: true,
            value: formUpdatedValues?.[key as keyof DetailsFormValues],
          };
        }
        return {
          name: key,
          touched: false,
          value: updatedFormValues[key as keyof DetailsFormValues],
        };
      });
      // only update field values without touching them as setFieldsValue touches all fields
      form.setFields([...fields, ...customFormFields]);
    },
    [form]
  );

  const handleCompaniesChange = useCallback(
    (companies: Company[]) => {
      form.setFields([
        {
          name: PersonFieldName.COMPANIES,
          touched: true,
          value: companies,
        },
      ]);
      handleFormChange();
    },
    [form, handleFormChange]
  );

  const saveButtonRef = useRef<HTMLElement>(null);
  const [formFieldsRef, handleFinishFail] = useErroredFieldsScroller();
  const isSaveButtonVisible = useOnScreen(saveButtonRef);

  const handleCustomFieldValidate = useCustomFieldValidation(form, handleFinishFail);

  const handleUndo = useCallback(() => {
    setPrimaryCompanyId(person.primaryAccountId ?? undefined);
    form.resetFields();
    onFormChange(false);
  }, [form, onFormChange, person.primaryAccountId]);

  const handleSave = useCallback(() => form.submit(), [form]);

  const handleFormFinish = useCallback(
    (formData) => {
      const ownerChanged = person.user.id !== formData.user;
      showConfirmationIfOwnerChanged(intl, ownerChanged, isOwner, () => {
        const addressPayload = getAddress(formData, person, intl);
        const personPayload = getPersonPayloadFromFormData(formData);

        const [groupIdsToAdd, groupIdsToRemove] = findDifference(
          (person.groups ?? []).map(({ id }) => id),
          Array.from(formData.groups ?? [])
        );

        const payload = {
          callback: (updatedPerson: Person) => {
            onFormChange(false);
            updateFormValues(updatedPerson);
            if (ownerChanged && !isOwner) {
              onHide();
            }
          },
          customFields: formData.customFields,
          customFieldsValueValidateCallback: handleCustomFieldValidate,
          groupIdsToAdd,
          groupIdsToRemove,
          layoutId: formLayout.id,
          person: {
            id: person.id,
            ...addressPayload,
            ...personPayload,
            primaryAccount: primaryCompanyId ? { id: primaryCompanyId } : null,
          },
        };
        onUpdatePerson(payload);
      });
    },
    [
      person,
      onHide,
      intl,
      isOwner,
      handleCustomFieldValidate,
      formLayout.id,
      primaryCompanyId,
      onFormChange,
      onUpdatePerson,
      updateFormValues,
    ]
  );

  useEffect(() => {
    const inheritFrom = person.primaryAccount ?? companies[0];
    setShowInheritButton(
      companies.length > 0 &&
        !(person.address && person.geoPoint) &&
        !!(inheritFrom.address && inheritFrom.geoPoint)
    );
  }, [setShowInheritButton, person, companies.length, companies]);

  useEffect(() => {
    form.setFieldValue(PersonFieldName.COMPANIES, companies);
  }, [companies, form]);

  const handleInheritClick = useCallback(() => {
    if (companies.length) {
      inheritAddressFromCompany(form, companies[0]);
      onFormChange(true);
      setShowInheritButton(false);
    }
  }, [companies, form, onFormChange]);

  const handleChangeParentCompany = useCallback(
    (primaryCompanyId?: Company["id"]) => setPrimaryCompanyId(primaryCompanyId),
    []
  );

  // This is impossible to set span dynamically yet, so we have to use JS solution to calculate it
  // There's a discussion in W3C to support that eventually: https://github.com/w3c/csswg-drafts/issues/1373
  const threshold = 2 * parseInt(consts.breakpointDetailsHalf) + parseInt(consts.gridGap);
  const [dividerSpan, containerRef] = useDividerSpan(threshold);

  const getRowClassName = useCallback(
    (schemaField?: LayoutSchemaField) => {
      const isDivider = !schemaField || schemaField.fieldType === SchemaFieldType.DIVIDER;
      return cn(styles.rowClassName, {
        [styles.dividerRowSpan2]: isDivider && dividerSpan === 2,
        [styles.dividerRowSpan3]: isDivider && dividerSpan === 3,
      });
    },
    [dividerSpan]
  );

  const handleDelete = useCallback(() => {
    handleUndo();
    onDelete();
  }, [handleUndo, onDelete]);

  useEffect(() => {
    eventEmitter.on("savePersonChanges", handleSave);
    return () => {
      eventEmitter.off("savePersonChanges", handleSave);
    };
  }, [handleSave]);

  const ref = useSharedRef(formFieldsRef, containerRef);
  const layoutsLength = personLayoutModel.layouts.length;
  const allowedOwnerUsers = useAllowedUsers(allOrgUsers, person, allUsersAllowedForOwnership);

  useEffect(() => {
    const formUpdatedValues = form.getFieldsValue(true, ({ touched }) => touched);
    updateFormValues(person, formUpdatedValues);
  }, [form, formChanged, person, updateFormValues]);

  useEffect(() => {
    setPrimaryCompanyId(person.primaryAccountId ?? undefined);
  }, [person.primaryAccountId]);

  return (
    <TabLayout
      action={
        <Button
          disabled={!formChanged || cannotSave}
          loading={personUpdateLoading}
          onClick={handleSave}
          ref={saveButtonRef}
          type="primary"
        >
          {formChanged
            ? intl.formatMessage(messages.saveChanges)
            : intl.formatMessage(messages.saved)}
        </Button>
      }
      hasChanges={formChanged}
      title={intl.formatMessage(messages.title)}
    >
      <div ref={ref}>
        {layoutsLength > 1 && (
          <FormLayoutSelector
            dividerClassName={styles.divider}
            entityType={EntityType.PERSON}
            onChange={setFormLayout}
            value={formLayout}
          />
        )}
        <Form
          form={form}
          initialValues={personToFormValues(person)}
          layout="vertical"
          onFieldsChange={handleFormChange}
          onFinish={handleFormFinish}
          onFinishFailed={handleFinishFail}
          preserve
        >
          {formLayout.id !== 0 ? (
            <FormFields
              appendAddableFieldsWithFilter={allOfThem}
              autoFocus={false}
              className={styles.fieldsContainer}
              disableAllFields={blankRequiredPrivateFields.length > 0 || isReadOnlyMember}
              disallowAdding
              entityType={EntityType.PERSON}
              filterFields={excludeUnnecessaryFields}
              layout={formLayout}
              rowClassName={getRowClassName}
            >
              <ConditionalFormField fieldName={CompanyFieldName.ADDRESS}>
                {({ disabled, required }: ConditionalFormFieldRenderProps) => (
                  <>
                    <AddressFormItem
                      disabled={disabled}
                      entity={person}
                      formChanged={formChanged}
                      isMapMode={isMapMode}
                      onAddressChange={onAddressChange}
                      onChange={onFormChange}
                      onEnterPinLocationMode={onEnterPinLocationMode}
                      required={required}
                    />
                    {showInheritButton && (
                      <div className={styles.autoFill}>
                        <ButtonLink onClick={handleInheritClick}>
                          {intl.formatMessage(
                            {
                              id: "personRecordPane.address.inheritFromCompanyButton",
                              defaultMessage: "Auto-fill address from {companyName}",
                              description:
                                "Inherit location from Company button text on the Record Pane",
                            },
                            { companyName: person.primaryAccount?.name ?? companies[0].name }
                          )}
                        </ButtonLink>
                      </div>
                    )}
                  </>
                )}
              </ConditionalFormField>

              <ConditionalFormField fieldName={PersonFieldName.COMPANIES}>
                <PersonCompanyField
                  onChange={handleCompaniesChange}
                  onChangePrimaryCompany={handleChangeParentCompany}
                  primaryCompanyId={primaryCompanyId}
                />
              </ConditionalFormField>

              <ConditionalFormField fieldName={CompanyFieldName.COLOR}>
                {({ disabled, label, required }: ConditionalFormFieldRenderProps) => (
                  <Form.Item
                    label={label}
                    name={CompanyFieldName.COLOR}
                    required={required}
                    rules={[{ required }]}
                  >
                    <CircleColorPicker disabled={disabled} />
                  </Form.Item>
                )}
              </ConditionalFormField>

              <ConditionalFormField fieldName={CompanyFieldName.PHONE}>
                {({ disabled, label, required }: ConditionalFormFieldRenderProps) => (
                  <Form.Item
                    label={label}
                    name={CompanyFieldName.PHONE}
                    required={required}
                    rules={[{ required }]}
                  >
                    <PhoneTextFieldWithActions disabled={disabled} person={person} />
                  </Form.Item>
                )}
              </ConditionalFormField>

              <ConditionalFormField fieldName={CompanyFieldName.EMAIL}>
                {({ disabled, label, required }: ConditionalFormFieldRenderProps) => (
                  <Form.Item
                    label={label}
                    name={CompanyFieldName.EMAIL}
                    required={required}
                    rules={[{ required }]}
                  >
                    <EmailTextFieldWithActions disabled={disabled} entity={person} />
                  </Form.Item>
                )}
              </ConditionalFormField>

              <ConditionalFormField fieldName={CompanyFieldName.USER}>
                {({ disabled, label, required }: ConditionalFormFieldRenderProps) => (
                  <Form.Item
                    label={label}
                    name={CompanyFieldName.USER}
                    required={required}
                    rules={[{ required }]}
                  >
                    <OwnerField disabled={disabled} user={person?.user} users={allowedOwnerUsers} />
                  </Form.Item>
                )}
              </ConditionalFormField>
            </FormFields>
          ) : (
            <FormNoLayoutAlert />
          )}
        </Form>
        <AdditionalDetails entity={person} />
        <DeleteButton deleting={deleting} entityType={EntityType.PERSON} onDelete={handleDelete} />
        {!cannotSave && (
          <SaveChangesFooter
            onSave={handleSave}
            onUndo={handleUndo}
            saving={personUpdateLoading}
            visible={formChanged && !isSaveButtonVisible}
          />
        )}
        {person.accessStatus?.update && (
          <HistoryButton entity={person} entityType={EntityType.PERSON} />
        )}
      </div>
    </TabLayout>
  );
};

const mapStateToProps = (state: RootState) => ({
  allOrgUsers: getUsersOfEntireOrganization(state),
  allUsersAllowedForOwnership:
    getOrganizationSettingValue(state)(OrganizationSetting.ALLOW_OWNERSHIP_TO_ALL_USERS, {
      [EntityType.PERSON]: false,
    })?.[EntityType.PERSON] ?? false,
  deleting: isDeleting(state),
  isOwner: isCurrentUserOwner(state),
  isReadOnlyMember: isCurrentUserReadOnly(state),
});

const mapDispatchToProps = {
  onDelete: deletePerson.request,
  onUpdatePerson: updatePerson.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(Details);
